import { getDownloadURL, ref, uploadBytes, deleteObject, listAll } from "firebase/storage";
import { storage } from "config/firebaseWithFirestore";

export async function uploadAsync({ path, file }) {
    const rf = ref(storage, path);
    return uploadBytes(rf, file);
}

export async function deleteAsync({ path }) {
    const rf = ref(storage, path);
    return deleteObject(rf);
}

/**
 * Copies a stored file (identified by its public download URL) into a new
 * storage path and returns the new download URL. Firebase Storage has no
 * server-side copy, so the bytes are downloaded and re-uploaded (bucket CORS
 * allows GET, see firebase_storage_cors.json).
 */
export async function copyAsync({ sourceUrl, destinationPath }) {
    const response = await fetch(sourceUrl);
    if (!response.ok) {
        throw new Error("Could not download the source image to clone it.");
    }
    const blob = await response.blob();
    const rf = ref(storage, destinationPath);
    const snap = await uploadBytes(rf, blob);
    return getDownloadURL(snap.ref);
}

export async function downloadAsync({ dirName, fileName }) {
    return new Promise(async (resolve, reject) => {
        try {
            const rf = ref(storage, `${dirName}/${fileName}`);
            const url = await getDownloadURL(rf);
            let blob = null;
            const xhr = new XMLHttpRequest();
            xhr.responseType = "blob";
            xhr.onload = function (event) {
                blob = xhr.response;
                if (blob) {
                    blob.lastModifiedDate = new Date();
                    blob.name = fileName;
                    return resolve(blob);
                } else {
                    return resolve(null);
                }
            };
            xhr.open("GET", url);
            xhr.send();
        } catch ({ message }) {
            reject(message);
        }
    });
}

export async function getDownloadURLAsync({ dirName, fileName }) {
    const rf = ref(storage, `${dirName}/${fileName}`);
    return getDownloadURL(rf);
}

// Función para listar contenidos de un "directorio"
export async function listDirectoryContents(directoryPath, { fetchUrls = true } = {}) {
    try {
        const listRef = ref(storage, directoryPath);

        // listAll() lista todos los elementos (archivos y prefijos)
        // directamente bajo la ruta especificada.
        const result = await listAll(listRef);

        const directories = [];
        const files = [];

        // 'prefixes' son los "subdirectorios"
        result.prefixes.forEach((folderRef) => {
            // El .name de folderRef es el nombre del "subdirectorio"
            directories.push(folderRef.name);
        });

        // 'items' son los archivos
        for (const itemRef of result.items) {
            if (fetchUrls) {
                // itemRef.name es el nombre del archivo
                // Puedes obtener la URL de descarga si lo necesitas
                const fileURL = await getDownloadURL(itemRef);
                files.push({ name: itemRef.name, url: fileURL, fullPath: itemRef.fullPath });
            } else {
                files.push({ name: itemRef.name, fullPath: itemRef.fullPath });
            }
        }

        console.log(`Contenidos de "${directoryPath}":`);
        console.log("Subdirectorios:", directories);
        console.log("Archivos:", files);

        return { directories, files };
    } catch (error) {
        console.error("Error al listar los contenidos:", error);
        return { directories: [], files: [] };
    }
}

// Ejemplos de uso:

// Para listar lo que hay en la "raíz" de tu Storage
// listDirectoryContents(""); // Esto listaría las "carpetas" de primer nivel (ej: "documentos", "imagenes")

// Para listar los contenidos de "documentos/"
// listDirectoryContents("documentos"); // Esto podría devolver "contratos" (como prefijo) y archivos como "readme.txt"

// Para listar los contenidos de "documentos/contratos/"
// listDirectoryContents("documentos/contratos"); // Esto te daría los archivos dentro de "contratos"

/**
 * Función para "crear" una carpeta vacía en Firebase Storage
 * subiendo un archivo placeholder.
 * @param {string} folderPath La ruta de la carpeta que se desea crear (ej. "documentos/nueva_carpeta").
 */
export async function createEmptyFolder(folderPath) {
    try {
        // 1. Define el nombre del archivo placeholder
        const placeholderFileName = ".placeholder"; // O '.keep', '_folder_marker', etc.

        // 2. Construye la ruta completa para el archivo placeholder
        // Asegúrate de que folderPath no termine con '/' si ya tiene archivos para evitar dobles barras
        const fullPath = `${folderPath.endsWith("/") ? folderPath : folderPath + "/"}${placeholderFileName}`;
        const storageRef = ref(storage, fullPath);

        // 3. Crea un Blob vacío o con contenido mínimo
        const emptyBlob = new Blob([], { type: "text/plain" }); // Un Blob vacío de tipo texto

        // 4. Sube el Blob como el archivo placeholder
        await uploadBytes(storageRef, emptyBlob);

        console.log(`Carpeta "${folderPath}" simulada exitosamente con el archivo: ${fullPath}`);
        return true;
    } catch (error) {
        console.error(`Error al simular la creación de la carpeta "${folderPath}":`, error);
        return false;
    }
}

// Ejemplo de uso:
// createEmptyFolder("clientes/cliente_b/facturas_2024");
// createEmptyFolder("proyectos/proyecto_alpha/recursos");

// Después de ejecutar esto, si usas listDirectoryContents("clientes/cliente_b/")
// deberías ver "facturas_2024" como uno de los directorios.

/**
 * Mueve un archivo de una ruta a otra en Firebase Storage.
 * Importante: Esto descarga y luego vuelve a subir el archivo.
 * Para archivos grandes, considera usar Cloud Functions.
 *
 * @param originalPath La ruta completa del archivo original (ej. 'fotos/vieja/imagen.jpg').
 * @param newPath La nueva ruta completa para el archivo (ej. 'fotos/nueva/imagen.jpg').
 */
export async function moveFile(originalUrl, newPath) {
    const originalFileRef = ref(storage, originalUrl);
    const newFileRef = ref(storage, newPath);

    try {
        // 1. Descargar el archivo original como un Blob usando la URL conocida
        const response = await fetch(originalUrl);
        const blob = await response.blob();

        // 2. Subir el Blob a la nueva ubicación
        const uploadResult = await uploadBytes(newFileRef, blob);

        // 3. Obtener la nueva URL de descarga
        const newUrl = await getDownloadURL(uploadResult.ref);

        // 4. Eliminar el archivo original
        await deleteObject(originalFileRef);

        return { uploadResult, newUrl };
    } catch (error) {
        console.error("Error al mover el archivo:", error);
        throw error;
    }
}

// Ejemplo de uso:
// moveFile('users/juan/avatar.png', 'users/juan/profile_picture.png')
//   .then(() => console.log('Operación de mover completada.'))
//   .catch(err => console.error('Fallo al mover:', err));
